/**
 * A company's card on the Family of Companies overview from `lg` up: a 360px square showing the
 * company image under a dark gradient with the logo on top. On hover the image and logo give way
 * to a solid blue panel with the company blurb and a "Learn More" button. The whole card links to
 * `/about-us/family-of-companies/<slug>/`, and the button is a second link to the same page
 * inside it.
 *
 * Rendered by `./FoCCategories.tsx`; styles are in `./CompanyCard.styled.tsx`. The mobile card is
 * `./CompanyCardMobile.tsx`.
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import { useTheme } from "@ryerson/frontend.theme";
 
import { css } from "@emotion/react";
import { Typography } from "@ryerson/frontend.typography";
import { FoCCompanyContent } from "@components/FamilyOfCompanies/FamilyOfCompaniesRefactor/Company";
import { LanguageContent } from "@components/Shared/model/LanguageContent";
import { ButtonLink } from "@ryerson/frontend.navigation";
import { Link } from "@ryerson/frontend.navigation";
import {
	ButtonLinkContainer,
	CardContainerWithHover,
	CSSGradientLayers,
	CSSLogoWithDropShadow,
	LogoContainerWithHover,
	TextContainerWithHover,
} from "./CompanyCard.styled";

type StaticFoCContent = {
	learnMore: string;
};

const StaticContent: LanguageContent<StaticFoCContent> = {
	en: {
		learnMore: "Learn More",
	},
	fr: {
		learnMore: "En savoir plus",
	},
	es: {
		learnMore: "Aprende más",
	},
};

/**
 * Props of {@link CompanyCard}. `company` is typed with the older `FoCCompanyContent`, but
 * `FoCCategories.tsx` passes a `ProcessedContentfulFoCCompanyFieldsForPreviews`
 * (`src/contentful/family-of-companies/types/index.ts`) through `any`. The card reads only `slug`,
 * `logo`, `mainFoCImage`, `companyName` and `mainFoCBlurb`, which both shapes have.
 */
export type CompanyProps = {
	company: FoCCompanyContent;
};
 

/** Renders one desktop company card; the button label follows the current language. */
const CompanyCard: React.FC<CompanyProps> = ({ company }) => {
	const { theme } = useTheme();

	const {
		localization: { language },
	} = useApplication();

	return (
		<Link to={"/about-us/family-of-companies/" + company.slug} next={true}>
			<CardContainerWithHover
				className="company-container"
				css={CSSGradientLayers({ company, theme })}
			>
				<LogoContainerWithHover className="logo-container" theme={theme}>
					<img
						src={company.logo.file.url}
						alt={company.companyName}
						css={CSSLogoWithDropShadow}
						className="logo"
					/>
				</LogoContainerWithHover>
				<TextContainerWithHover className="text-container">
					<Typography
						size="lg"
						color={theme.colors.primary.white}
						css={css`
							margin-top: 40px;
						`}
					>
						{company.mainFoCBlurb}
					</Typography>
					<ButtonLinkContainer>
						<ButtonLink
							label={StaticContent[language].learnMore}
							to={"/about-us/family-of-companies/" + company.slug}
							size="sm"
							type="tertiary"
							next={true}
						/>
					</ButtonLinkContainer>
				</TextContainerWithHover>
			</CardContainerWithHover>
		</Link>
	);
};

export default CompanyCard;
